import React from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Stack,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StatusChip from './StatusChip';
import RowThumb from './RowThumb';
import CategoryPill from './CategoryPill';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';

// Full line-item breakdown for one order — unit price, quantity, subtotal,
// any per-line coupon discount, and the customer's note. Shared by the
// customer "My Orders" page and the admin "Client Orders" page so both sides
// of the app see identical order detail; update once here, both stay in
// sync. `t`/`i18n` are passed in rather than read via useTranslation so this
// stays a plain prop-driven component like every other ui/ dialog.
//
// Coupons apply per line and a line can carry more than one, so `discount`
// is only ever shown per item/order, never split back out per coupon — the
// backend doesn't hand back which coupon contributed how much when several
// stack on one line.
const OrderDetailDialog = ({ order, open, onClose, t, i18n }) => {
  const items = order?.orderItems?.length
    ? order.orderItems
    : order
      ? [{ productName: order.productName, quantity: order.quantity, unitPrice: order.totalPrice / (order.quantity || 1), subtotal: order.totalPrice }]
      : [];

  const orderHasDiscount = items.some((item) => (item.discount || 0) > 0);
  const subtotalBeforeDiscount = items.reduce((sum, item) => sum + (item.originalPrice ?? item.subtotal ?? 0), 0);
  const totalDiscount = items.reduce((sum, item) => sum + (item.discount || 0), 0);
  const appliedCoupons = Array.from(
    new Map(items.flatMap((item) => item.appliedCoupons || []).map((c) => [c.code || c.couponId, c])).values()
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      {order && (
        <>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {t('Order #')}{order.orderNumber}
            <StatusChip status={order.status} label={t(order.status)} />
          </DialogTitle>
          <DialogContent>
            {(order.createdAt || order.username) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 2 }}>
                {order.createdAt && new Date(order.createdAt).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' })}
                {order.createdAt && order.username && ' · '}
                {order.username}
              </Typography>
            )}

            <Stack divider={<Divider />} spacing={1.5}>
              {items.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <RowThumb name={item.productName} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{item.productName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.quantity} × {formatCurrency(item.unitPrice || 0, 'USD', i18n.language)}
                    </Typography>
                    {item.appliedCoupons?.length > 0 && (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, rowGap: 0.5 }}>
                        {item.appliedCoupons.map((coupon) => (
                          <CategoryPill
                            key={coupon.couponId || coupon.code}
                            color={coupon.color}
                            label={coupon.code}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    {(item.discount || 0) > 0 && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textDecoration: 'line-through' }}>
                        {formatCurrency(item.originalPrice, 'USD', i18n.language)}
                      </Typography>
                    )}
                    <Typography variant="body2" fontWeight={700}>{formatCurrency(item.subtotal ?? 0, 'USD', i18n.language)}</Typography>
                  </Box>
                </Box>
              ))}
            </Stack>

            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              {orderHasDiscount && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('Subtotal', { defaultValue: 'Subtotal' })}</Typography>
                    <Typography variant="body2">{formatCurrency(subtotalBeforeDiscount, 'USD', i18n.language)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', py: 0.5, gap: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" sx={{ color: 'success.main', rowGap: 0.5 }}>
                      <LocalOfferIcon fontSize="small" />
                      {appliedCoupons.map((coupon) => (
                        <Typography key={coupon.couponId || coupon.code} variant="body2" color="success.main">{coupon.code}</Typography>
                      ))}
                    </Stack>
                    <Typography variant="body2" color="success.main" fontWeight={700} sx={{ flexShrink: 0 }}>
                      −{formatCurrency(totalDiscount, 'USD', i18n.language)}
                    </Typography>
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: orderHasDiscount ? 1 : 0, pt: orderHasDiscount ? 1 : 0, borderTop: orderHasDiscount ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Typography variant="subtitle1">{t('Total')}</Typography>
                <Typography variant="subtitle1" fontWeight={700}>{formatCurrency(order.totalPrice, 'USD', i18n.language)}</Typography>
              </Box>
              {orderHasDiscount && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                  {t('You saved {{amount}}', { amount: formatCurrency(totalDiscount, 'USD', i18n.language), defaultValue: `You saved ${formatCurrency(totalDiscount, 'USD', i18n.language)}` })}
                </Typography>
              )}
            </Box>

            {order.message && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: `${radius.md}px`, border: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                <Typography variant="caption" color="text.secondary">{t('Customer note', { defaultValue: 'Customer note' })}</Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.25 }}>&quot;{order.message}&quot;</Typography>
              </Box>
            )}

            {order.eta && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">{t('Estimated Delivery')}</Typography>
                <Typography variant="body2">{new Date(order.eta).toLocaleDateString()}</Typography>
              </Box>
            )}

            {order.status === 'rejected' && order.rejectReason && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: `${radius.md}px`, bgcolor: 'error.light', color: 'error.contrastText' }}>
                <Typography variant="body2" fontWeight={700}>{t('Reject Reason')}</Typography>
                <Typography variant="body2">{order.rejectReason}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>{t('Close', { defaultValue: 'Close' })}</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default OrderDetailDialog;
